import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useBillingStore, SubscriptionPlan } from '../../store/billingStore';
import {
  X, Loader2, Crown, Server, Check, Sparkles, CreditCard, ChevronRight,
} from 'lucide-react';

interface Props {
  onClose: () => void;
  /** Pre-filter by type */
  filterType?: 'erozion_plus' | 'hosting';
}

const typeIcon: Record<string, React.ElementType> = {
  erozion_plus: Crown,
  hosting: Server,
};

const typeGradient: Record<string, string> = {
  erozion_plus: 'from-purple-500/20 to-accent-color/20',
  hosting: 'from-green-500/20 to-emerald-500/20',
};

const typeAccent: Record<string, string> = {
  erozion_plus: 'text-purple-400',
  hosting: 'text-emerald-400',
};

const typeBorder: Record<string, string> = {
  erozion_plus: 'border-purple-500/30 hover:border-purple-500/60',
  hosting: 'border-emerald-500/30 hover:border-emerald-500/60',
};

const SubscriptionPlansModal: React.FC<Props> = ({ onClose, filterType }) => {
  const { plans, isLoadingPlans, fetchPlans, openPaymentDrawer } = useBillingStore();

  useEffect(() => {
    fetchPlans(filterType);
  }, [filterType]);

  const handleSubscribe = (plan: SubscriptionPlan) => {
    if (plan.is_subscribed) return;

    // Open the PaymentDrawer with the subscription plan
    openPaymentDrawer({
      type: plan.type === 'hosting' ? 'hosting_subscription' : 'erozion_plus',
      itemId: plan.id,
      itemName: plan.display_name,
      amount: Number(plan.price_monthly),
      currency: plan.currency,
      stripePriceId: plan.stripe_price_id || undefined,
      description: plan.trial_days > 0
        ? `${plan.trial_days} jours d'essai gratuit, puis ${Number(plan.price_monthly).toFixed(2)}$/mois`
        : `${Number(plan.price_monthly).toFixed(2)}$/mois`,
    });
    onClose();
  };

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center bg-black/70 backdrop-blur-sm animate-fade-in"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="relative bg-bg-primary rounded-2xl w-full max-w-2xl border border-border-color shadow-2xl shadow-black/50 animate-slide-up overflow-hidden max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border-color shrink-0">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-accent-color" />
            <h2 className="text-base font-bold text-text-primary">Abonnements</h2>
          </div>
          <button
            onClick={onClose}
            className="w-7 h-7 rounded-lg bg-bg-tertiary/80 border border-border-color flex items-center justify-center text-text-muted hover:text-text-primary transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto">
          {isLoadingPlans ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-6 w-6 animate-spin text-text-muted" />
            </div>
          ) : plans.length === 0 ? (
            <p className="text-center text-text-muted text-sm py-12">
              Aucun plan disponible pour le moment.
            </p>
          ) : (
            <div className="grid gap-4 md:grid-cols-2">
              {plans.map((plan) => {
                const Icon = typeIcon[plan.type] || Crown;
                const gradient = typeGradient[plan.type] || typeGradient.erozion_plus;
                const accent = typeAccent[plan.type] || typeAccent.erozion_plus;
                const border = typeBorder[plan.type] || typeBorder.erozion_plus;

                return (
                  <div
                    key={plan.id}
                    className={`relative rounded-2xl border ${border} bg-bg-secondary/60 overflow-hidden transition-all duration-200`}
                  >
                    {/* Gradient top */}
                    <div className={`h-24 bg-gradient-to-br ${gradient} flex items-center justify-center`}>
                      <Icon className={`h-10 w-10 ${accent}`} />
                    </div>

                    <div className="p-5 space-y-4">
                      {/* Title + Price */}
                      <div>
                        <h3 className="text-lg font-bold text-text-primary">{plan.display_name}</h3>
                        {plan.description && (
                          <p className="text-xs text-text-muted mt-1">{plan.description}</p>
                        )}
                        <div className="mt-3 flex items-baseline gap-1">
                          <span className="text-3xl font-bold text-text-primary tabular-nums">
                            {Number(plan.price_monthly).toFixed(2)}$
                          </span>
                          <span className="text-sm text-text-muted">/ mois</span>
                        </div>
                        {plan.trial_days > 0 && (
                          <p className={`text-xs font-semibold mt-1 ${accent}`}>
                            {plan.trial_days} jours d'essai gratuit
                          </p>
                        )}
                      </div>

                      {/* Features */}
                      <ul className="space-y-2">
                        {(plan.features || []).map((f, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-text-secondary">
                            <Check className={`h-4 w-4 shrink-0 mt-0.5 ${accent}`} />
                            {f}
                          </li>
                        ))}
                      </ul>

                      {/* Subscribe button */}
                      {plan.is_subscribed ? (
                        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-success/10 text-success text-sm font-semibold border border-success/20">
                          <Check className="h-4 w-4" />
                          Abonné
                        </div>
                      ) : (
                        <button
                          onClick={() => handleSubscribe(plan)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-accent-color text-black text-sm font-bold hover:bg-accent-hover transition-colors"
                        >
                          <CreditCard className="h-4 w-4" />
                          {plan.trial_days > 0 ? 'Commencer l\'essai gratuit' : 'S\'abonner'}
                          <ChevronRight className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SubscriptionPlansModal;
